<script setup lang="ts">
import { type Component, computed, nextTick, onUnmounted, ref } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";
import { useRouter } from "vue-router";

import actions from "~/actions";
import { backendOld } from "~/api";
import { getPropertyConfig, getPropertyDisplayTitle, getPropertyValueFromTask } from "~/common/properties";
import AddAgentOrTeammate from "~/components/dumb/AddAgentOrTeammate.vue";
import AiSpinner from "~/components/dumb/AiSpinner.vue";
import Avatar from "~/components/dumb/Avatar.vue";
import AvatarGroup from "~/components/dumb/AvatarGroup.vue";
import Button from "~/components/dumb/Button.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import EntityPreviewTooltip from "~/components/dumb/EntityPreviewTooltip.vue";
import FillOutWithAiItem from "~/components/dumb/FillOutWithAiItem.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import UserDropdownItem from "~/components/dumb/UserDropdownItem.vue";
import { notify } from "~/components/notifications";
import {
  DEACTIVATED_USER_LABEL,
  UNASSIGNED_ASSIGNEE_LABEL,
  UNSET_USER_LABEL,
} from "~/components/visualization/constants";
import { createAgentNoOptionAction, makeInviteByEmailNoOptionAction } from "~/constants/noOptionActions";
import { UNSET_PROPERTY_PSEUDO_VALUE } from "~/constants/pseudoDuids";
import { colorsByTheme } from "~/constants/style";
import {
  AgentIcon,
  AssigneeFieldIcon,
  CreatedByFieldIcon,
  LastUpdatedByFieldIcon,
  OpenInternalLinkIcon,
  ReviewerFieldIcon,
  StopIcon,
  UserFieldIcon,
  XIcon,
} from "~/icons";
import {
  ButtonSize,
  ButtonStyle,
  CommandId,
  EditorMode,
  IconSize,
  NotificationType,
  Placement,
  PropertyKind,
  UserRole,
} from "~/shared/enums";
import type {
  MultiselectDropdownMenuItem,
  MultiselectDropdownMenuSection,
  NoOptionAction,
  PropertyAnyUser,
  PropertyAnyUserWithMultipleMaybe,
  PropertyValueForKind,
  Task,
  User,
} from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useUserStore } from "~/stores";
import { getUserPageLink, intersectionOfAllSets, unionOfAllSets } from "~/utils/common";
import { agentComparator } from "~/utils/comparator";
import { generateFieldRecommendation } from "~/utils/recommendation";
import {
  getUserDisplayName,
  isAgentInactive,
  isAgentSetupRequired,
  isAgentUser,
  isInaccessibleAgent,
  splitCurrentUserItem,
} from "~/utils/user";

const PROPERTY_ICON_MAP = new Map<PropertyKind, Component>([
  [PropertyKind.DEFAULT_ASSIGNEES, AssigneeFieldIcon],
  [PropertyKind.DEFAULT_REVIEWERS, ReviewerFieldIcon],
  [PropertyKind.DEFAULT_CREATED_BY, CreatedByFieldIcon],
  [PropertyKind.DEFAULT_UPDATED_BY, LastUpdatedByFieldIcon],
  [PropertyKind.USER, UserFieldIcon],
]);

const props = defineProps<{
  property: PropertyAnyUser;
  tasks: Task[];
  defaultValue?: PropertyValueForKind<PropertyAnyUser>;
  showText?: boolean;
  showTextSingle?: boolean;
  editorMode: EditorMode;
  values?: string[];
  onAfterClose?: (value: string[]) => void;
  agentsOnly?: boolean;
}>();

const emit = defineEmits<{
  update: [userDuids?: string[]];
  accept: [];
  reject: [];
}>();

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);

const dropdownMenu = ref<ComponentExposed<typeof MultiselectDropdownMenu> | null>(null);
const feedbackTooltipNode = ref<HTMLDivElement | null>(null);

const propertyConfig = computed(() => getPropertyConfig(props.property.kind));
const isDefault = computed(() => propertyConfig.value.isDefault);
const isAssignee = computed(() => props.property.kind === PropertyKind.DEFAULT_ASSIGNEES);
const isReviewer = computed(() => props.property.kind === PropertyKind.DEFAULT_REVIEWERS);
const isReadOnly = computed(() => propertyConfig.value.readOnly);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const isFormOrAutomationMode = computed(() => isFormMode.value || isAutomationMode.value);
const canSelectAgents = computed(() => !isReadOnly.value && (isFormMode.value || userStore.isRoleGte(UserRole.MEMBER)));
const canAddUserOrAgent = computed(
  () => !isReadOnly.value && !pageStore.isPublicForm && userStore.isRoleGte(UserRole.MEMBER)
);

const isPropertyNameShown = computed(
  () => "adtl" in props.property && props.property.adtl.isNameShown && !pageStore.isMobile
);
const multipleUsersEnabled = computed(
  () => "adtl" in props.property && "isMultiple" in props.property.adtl && props.property.adtl.isMultiple
);

const isBaseListMode = computed(() => props.editorMode === EditorMode.LIST);
const isListMiniMode = computed(() => props.editorMode === EditorMode.LIST_MINI);
const isListMiniTcmMode = computed(() => props.editorMode === EditorMode.LIST_MINI_TCM);
const isListMiniNonRoadmapMode = computed(() => isListMiniMode.value || isListMiniTcmMode.value);
const isListMode = computed(
  () => isBaseListMode.value || isListMiniNonRoadmapMode.value || props.editorMode === EditorMode.LIST_MINI_ROADMAP
);
const isChipRecommendationMode = computed(() => props.editorMode === EditorMode.CHIP_RECOMMENDATION);
const isBaseChipMode = computed(() => props.editorMode === EditorMode.CHIP);
const isChipMode = computed(() => isBaseChipMode.value || isChipRecommendationMode.value);
const isBoardMode = computed(() => props.editorMode === EditorMode.BOARD);
const isDetailBaseMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isDetailRecommendationMode = computed(() => props.editorMode === EditorMode.DETAIL_RECOMMENDATION);
const isTaskDetailMode = computed(() => isDetailBaseMode.value || isDetailRecommendationMode.value);
const isRecommendationMode = computed(() => isChipRecommendationMode.value || isDetailRecommendationMode.value);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);

const getSelectedTaskDuids = () => {
  if (isListMiniNonRoadmapMode.value) {
    const selectedDuids =
      appStore
        .getSubtasksListVisualization()
        ?.getSelectedRows()
        .map((row) => row.data?.duid)
        .filter((taskDuid): taskDuid is string => !!taskDuid) ?? [];
    return selectedDuids.length > 0 ? selectedDuids : props.tasks.map((e) => e.duid);
  }
  if (isBaseListMode.value) {
    return [...appStore.selectedTaskDuids];
  }
  return props.tasks.map((task) => task.duid);
};

const selectedTasks = computed(() =>
  dataStore.getTasksByDuidsOrderedIfLoaded(getSelectedTaskDuids(), { includeTrashed: false, includeDraft: true })
);
const selectedTaskDuids = computed(() => selectedTasks.value.map((e) => e.duid));

const userDuids = computed(() => {
  if (isFormOrAutomationMode.value) {
    return props.values ?? [];
  }

  if (isReadOnly.value) {
    const firstTaskValue = getPropertyValueFromTask(props.property, props.tasks[0]) as string | null;
    return firstTaskValue !== null &&
      props.tasks.every((task) => getPropertyValueFromTask(props.property, task) === firstTaskValue)
      ? [firstTaskValue]
      : [];
  }
  return [...unionOfAllSets(props.tasks.map((e) => new Set(getPropertyValueFromTask(props.property, e) as string[])))];
});

const showAgentWorking = computed(
  () =>
    isAssignee.value &&
    !(isChipMode.value || isChipRecommendationMode.value || isListMiniTcmMode.value) &&
    props.tasks.some((task) => dataStore.isTaskAgentWorking(task))
);

const singleAgentAssignee = computed(() => {
  if (userDuids.value.length !== 1) {
    return undefined;
  }
  const user = dataStore.getUserByDuid(userDuids.value[0]);
  return user && isAgentUser(user) && !isInaccessibleAgent(user) ? user : undefined;
});

const activeAgentSession = computed(() => {
  if (!singleAgentAssignee.value || props.tasks.length !== 1) {
    return undefined;
  }
  return singleAgentAssignee.value.agent?.activeSessions.find(
    (session) => session.active && session.taskDuid === props.tasks[0].duid
  );
});

const navigateToAgent = () => {
  if (!singleAgentAssignee.value) {
    return;
  }
  appStore.setTcmOpen(false);
  router.push(getUserPageLink(singleAgentAssignee.value));
};

const stoppingAgentSessionDuid = ref<string | null>(null);
const stopAgentSession = async () => {
  const session = activeAgentSession.value;
  if (!session) {
    return;
  }

  stoppingAgentSessionDuid.value = session.duid;
  try {
    await backendOld.agents.stopSession(session.duid);
  } finally {
    if (stoppingAgentSessionDuid.value === session.duid) {
      stoppingAgentSessionDuid.value = null;
    }
  }
};

const loading = computed(
  () => isDefault.value && !isReadOnly.value && appStore.isLoadingTasksProperty(props.tasks, props.property.duid)
);
const singleUserPreviewDuid = computed(() =>
  loading.value || userDuids.value.length !== 1 ? undefined : userDuids.value[0]
);
const defaultCommandId = computed(() => {
  if (!isDefault.value || isReadOnly.value || multipleUsersEnabled.value) {
    return undefined;
  }
  return isReviewer.value ? CommandId.CHANGE_REVIEWER : CommandId.CHANGE_ASSIGNEE;
});
const defaultCommandIds = computed(() => {
  if (!isDefault.value || isReadOnly.value || !multipleUsersEnabled.value) {
    return undefined;
  }
  return [isReviewer.value ? CommandId.EDIT_REVIEWERS : CommandId.EDIT_ASSIGNEES];
});
const defaultPlaceholder = computed(() => {
  if (props.agentsOnly) {
    return "Assign an agent";
  }
  if (!isDefault.value || isFormOrAutomationMode.value) {
    return "Add a user";
  }
  return isReviewer.value ? "Assign a reviewer" : "Assign a teammate";
});

const isEditableUserProperty = (property: PropertyAnyUser): property is PropertyAnyUserWithMultipleMaybe =>
  [PropertyKind.DEFAULT_ASSIGNEES, PropertyKind.DEFAULT_REVIEWERS, PropertyKind.USER].includes(property.kind);

const unsetUserLabel = computed(() => {
  if (isAssignee.value) {
    return isAutomationMode.value ? "Unassigned" : UNASSIGNED_ASSIGNEE_LABEL;
  }
  return UNSET_USER_LABEL;
});
const isUnsetSelected = computed(() => userDuids.value.includes(UNSET_PROPERTY_PSEUDO_VALUE));

const replaceUsers = (duids: string[]) => {
  if (!isEditableUserProperty(props.property)) {
    return;
  }

  if (isFormOrAutomationMode.value) {
    emit("update", duids);
    return;
  }

  const taskDuids = getSelectedTaskDuids();
  dataStore.replaceUsers(props.property, taskDuids, duids);
  emit("update");
};

const clearUsers = () => {
  replaceUsers([]);
  dropdownMenu.value?.close();
};

const onReplace = (duid?: string | null) => {
  if (duid === UNSET_PROPERTY_PSEUDO_VALUE || duid === null) {
    clearUsers();
    return;
  }

  replaceUsers(duid ? [duid] : []);

  if (!isAutomationMode.value) {
    dropdownMenu.value?.close();
  }
};

const addUser = (duid: string | null) => {
  if (duid === UNSET_PROPERTY_PSEUDO_VALUE || duid === null) {
    clearUsers();
    return;
  }

  if (!isEditableUserProperty(props.property)) {
    return;
  }

  if (!multipleUsersEnabled.value) {
    onReplace(duid);
    return;
  }

  if (isFormOrAutomationMode.value) {
    emit("update", [...new Set([...userDuids.value.filter((e) => e !== UNSET_PROPERTY_PSEUDO_VALUE), duid])]);
    return;
  }

  const taskDuids = getSelectedTaskDuids();
  dataStore.addUsers(props.property, taskDuids, [duid]);
  emit("update");
};
const allAgents = computed(() =>
  dataStore.getUserList({ excludeNonagents: true, includeInactiveAgents: true }).sort(agentComparator)
);
const activeAgents = computed(() => allAgents.value.filter((agent) => !isAgentInactive(agent)));

const onRemove = (duid: string | null) => {
  if (duid === UNSET_PROPERTY_PSEUDO_VALUE || duid === null) {
    clearUsers();
    return;
  }

  if (!isEditableUserProperty(props.property)) {
    return;
  }

  if (isFormOrAutomationMode.value) {
    emit(
      "update",
      userDuids.value.filter((e) => e !== duid)
    );
    return;
  }

  if (!multipleUsersEnabled.value) {
    onReplace();
    return;
  }

  const taskDuids = getSelectedTaskDuids();
  dataStore.removeUsers(props.property, taskDuids, [duid]);
  emit("update");
};

const noOptionActions = computed<NoOptionAction[]>(() => {
  const result: NoOptionAction[] = [];
  if (canAddUserOrAgent.value || props.agentsOnly) {
    result.push(createAgentNoOptionAction);
  }
  if (canAddUserOrAgent.value && !props.agentsOnly) {
    result.push(makeInviteByEmailNoOptionAction(addUser));
  }
  return result;
});

const generateRecommendation = async () => {
  const tempSelectedTasks = selectedTaskDuids.value;
  if (tempSelectedTasks.length > 100) {
    notify({ message: "Dart AI cannot fill out more than 100 tasks at once", type: NotificationType.ERROR });
    return;
  }

  appStore.startLoadingAiTaskProperties(tempSelectedTasks, props.property.duid);
  const [taskUpdates, recDuids] = await generateFieldRecommendation(
    selectedTasks.value,
    "assigneeDuids",
    props.defaultValue ?? []
  );
  dataStore.updateTasks(taskUpdates);
  appStore.finishLoadingAiTaskProperties(tempSelectedTasks, props.property.duid);

  nextTick(() => {
    actions.visualization.showFeedbackTooltipForTask(
      tempSelectedTasks,
      recDuids,
      props.editorMode,
      feedbackTooltipNode.value ?? null
    );
  });
};

const rejectRecommendation = () => {
  replaceUsers(Array.isArray(props.defaultValue) ? props.defaultValue : []);
  emit("reject");
};

const generateItemForUser = (user: User): MultiselectDropdownMenuItem<string> => {
  const value = user.duid;
  const isRealUser = !!dataStore.getUserByDuid(user.duid);
  const showNavigateIcon = isRealUser && isAgentUser(user) && !isInaccessibleAgent(user) && !pageStore.isPublic;

  const selectedUserDuids = isFormOrAutomationMode.value
    ? userDuids.value
    : [
        ...intersectionOfAllSets(
          selectedTasks.value.map((e) => getPropertyValueFromTask(props.property, e)).map((e) => new Set(e))
        ),
      ];
  return {
    value,
    label: getUserDisplayName(user),
    adtlSearchTerms: [user.email],
    selected: multipleUsersEnabled.value ? selectedUserDuids.includes(value) : selectedUserDuids[0] === value,
    component: UserDropdownItem,
    componentArgs: {
      replaceEnabled: multipleUsersEnabled.value,
      icon: Avatar,
      iconArgs: { user, class: "icon-lg" },
      user: isRealUser ? user : undefined,
      navigateEnabled: showNavigateIcon,
      navigateTooltip: showNavigateIcon ? "Open agent" : undefined,
      onNavigate: showNavigateIcon ? () => router.push(getUserPageLink(user)) : undefined,
    },
  };
};

const label = computed(() => {
  if (userDuids.value.length === 0) {
    return isAutomationMode.value
      ? "Set users"
      : isFormMode.value
        ? "Set users"
        : isDefault.value && !isReadOnly.value
          ? UNASSIGNED_ASSIGNEE_LABEL
          : "None";
  }
  if (isUnsetSelected.value) {
    return unsetUserLabel.value;
  }

  const user = dataStore.getUserByDuid(userDuids.value[0]);
  if (!user) {
    return isDefault.value && !isReadOnly.value ? UNASSIGNED_ASSIGNEE_LABEL : getPropertyDisplayTitle(props.property);
  }

  const singleUserMultiEnabledShowPropertyName =
    multipleUsersEnabled.value && isPropertyNameShown.value && userDuids.value.length === 1;

  return singleUserMultiEnabledShowPropertyName || !multipleUsersEnabled.value || userDuids.value.length === 1
    ? getUserDisplayName(user)
    : isDefault.value
      ? ""
      : "None";
});

const sections = computed(() => {
  const items: MultiselectDropdownMenuSection<string>[] = [];

  if (props.agentsOnly) {
    if (activeAgents.value.length > 0) {
      items.push({
        title: "Agents",
        showTitle: true,
        items: activeAgents.value.map((agent) => generateItemForUser(agent)),
      });
    }
    return items;
  }

  const excludeAgents = !canSelectAgents.value;
  const users = isFormOrAutomationMode.value
    ? dataStore.getUserList({
        excludeAgents,
      })
    : dataStore.getRelevantUsersByTasks(props.tasks, { excludeAgents });

  const subscriberDuids = new Set(props.tasks.flatMap((task) => task.subscriberDuids));
  const { currentUserItem, otherItems: usersWithoutCurrent } = splitCurrentUserItem(
    users,
    userStore.duid,
    (user) => user.duid,
    dataStore.getUserByDuid(userStore.duid)
  );
  const selectableUsers = usersWithoutCurrent.filter(
    (user) => !isAgentInactive(user) && (pageStore.isPublicForm || !isAgentSetupRequired(user))
  );
  const subscriberUsers = selectableUsers.filter((user) => subscriberDuids.has(user.duid));
  const allOtherUsers = selectableUsers.filter((user) => !subscriberDuids.has(user.duid));
  const relevantUsers = currentUserItem ? [currentUserItem, ...subscriberUsers] : subscriberUsers;

  if (relevantUsers.length > 0) {
    items.push({
      title: "Relevant",
      showTitle: true,
      items: relevantUsers.map((user) => generateItemForUser(user)),
    });
  }
  if (allOtherUsers.length > 0) {
    items.push({
      title: "All",
      showTitle: true,
      items: allOtherUsers.map((user) => generateItemForUser(user)),
    });
  }

  return items;
});

const handleAfterClose = () => {
  props.onAfterClose?.(userDuids.value);
};

onUnmounted(() => {
  appStore.showFeedbackTooltip(null, []);
});

defineExpose({
  open: () => dropdownMenu.value?.open(),
  isOpen: computed(() => dropdownMenu.value?.isOpen),
});
</script>

<template>
  <MultiselectDropdownMenu
    ref="dropdownMenu"
    :disabled="isReadOnly"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined"
    :sections="sections"
    :header-title="agentsOnly ? 'Assign to an agent' : `Set ${getPropertyDisplayTitle(property).toLowerCase()}`"
    :placeholder="`${defaultPlaceholder}...`"
    :item-classes="['group/user-dropdown-item']"
    :no-option-actions="noOptionActions"
    :block="isFormMode"
    :height-block="isFormOrAutomationMode"
    :distance="isFormOrAutomationMode ? 2 : undefined"
    :cover="!isContextMenuMode && !isFormOrAutomationMode"
    :placement="isContextMenuMode ? Placement.RIGHT_TOP : Placement.BOTTOM_LEFT"
    :close-on-select="!isAutomationMode && !multipleUsersEnabled"
    show-all
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }"
    :on-after-close="handleAfterClose"
    @add="addUser"
    @remove="onRemove"
    @replace="onReplace">
    <template v-if="isAssignee && !isReadOnly && !isFormOrAutomationMode" #footer="{ close }">
      <FillOutWithAiItem :close="close" @recommend="generateRecommendation" />
    </template>
    <template v-if="canAddUserOrAgent || agentsOnly" #extra="{ close }">
      <AddAgentOrTeammate
        :agents-only="agentsOnly"
        :close="close"
        :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined" />
    </template>
    <DropdownMenuItemContent
      v-if="isContextMenuMode"
      :icon="agentsOnly ? AgentIcon : PROPERTY_ICON_MAP.get(property.kind)"
      :title="agentsOnly ? 'Assign to an agent' : `Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      is-submenu />
    <Tooltip
      v-else
      :disabled="isFormOrAutomationMode"
      :class="isFormOrAutomationMode ? 'overflow-hidden' : ''"
      :command-id="defaultCommandId"
      :command-ids="defaultCommandIds"
      :text="
        isReadOnly
          ? getPropertyDisplayTitle(property)
          : isDefault
            ? undefined
            : `Change ${getPropertyDisplayTitle(property).toLowerCase()}`
      "
      :block="isListMode || isTaskDetailMode || isFormOrAutomationMode"
      :height-block="isListMode || isFormOrAutomationMode">
      <div
        ref="feedbackTooltipNode"
        class="flex size-full items-center select-none"
        :class="{
          'pr-2 pl-1.5': isTaskDetailMode,
          'pr-7 pl-2': isFormMode,
          'pl-2': isAutomationMode,
          'hover:bg-lt': (isFormOrAutomationMode || isTaskDetailMode) && !isReadOnly,
          'rounded whitespace-nowrap': !isListMode && !isTaskDetailMode,
          'hover:bg-md': !isListMode && !isRecommendationMode && !isFormOrAutomationMode && !isTaskDetailMode,
          'border border-oncolor': isBaseChipMode,
          'border border-recommendation-base/50 bg-recommendation-base/30 hover:bg-recommendation-base/40 dark:hover:bg-recommendation-base/40':
            isChipRecommendationMode,
          'px-1 py-0.5': !isListMode && !isTaskDetailMode && !isFormOrAutomationMode,
          'justify-start rounded px-2 py-1': isTaskDetailMode,
          'justify-start': isListMode && isPropertyNameShown && userDuids.length <= 1,
          'justify-center':
            !isTaskDetailMode &&
            !(isListMode && isPropertyNameShown && userDuids.length <= 1) &&
            !isFormOrAutomationMode,
          'items-end gap-2 bg-recommendation-base/10 hover:bg-recommendation-base/20 dark:hover:hover:bg-recommendation-base/20':
            isDetailRecommendationMode,
          'cursor-default': isReadOnly && !isBoardMode,
        }">
        <div v-if="!isListMode && !isTaskDetailMode && !showText && userDuids.length === 0" class="text-vlt">
          {{ isReadOnly ? "None" : `Set ${getPropertyDisplayTitle(property).toLowerCase()}` }}
        </div>
        <div v-if="loading" class="flex size-full items-center justify-center px-4 py-0.5">
          <AiSpinner class="icon-sm" />
        </div>
        <EntityPreviewTooltip
          v-if="!loading"
          :user-duid="singleUserPreviewDuid"
          class="items-center"
          :class="(isTaskDetailMode || isFormOrAutomationMode) && 'flex-1'">
          <AvatarGroup
            :duids="userDuids"
            :show-agent-working="showAgentWorking"
            :limit="isListMode ? 3 : undefined"
            :first-user-only="!multipleUsersEnabled"
            :large="isListMode"
            :editor-mode="editorMode"
            :unset-label="isReadOnly ? DEACTIVATED_USER_LABEL : !isDefault ? UNSET_USER_LABEL : undefined"
            :hide-unassigned-icon="isFormMode"
            :is-tooltip-disabled="Boolean(singleUserPreviewDuid)" />
          <span
            v-if="showText || isPropertyNameShown || isTaskDetailMode || (userDuids.length === 1 && showTextSingle)"
            class="text-sm"
            :title="label"
            :class="{
              'ml-1 max-w-xs truncate': isChipMode,
              truncate: (isListMode && isPropertyNameShown) || isAutomationMode,
              'break-words flex-1 text-left hyphens-auto': isTaskDetailMode,
              'truncate pr-5': isFormMode,
              'ml-2': !isChipMode && !(isFormOrAutomationMode && userDuids.length === 0),
              'text-vlt': ((isFormOrAutomationMode || isTaskDetailMode) && userDuids.length === 0) || isUnsetSelected,
            }">
            {{ label }}
          </span>
        </EntityPreviewTooltip>
        <Tooltip
          v-if="isTaskDetailMode && activeAgentSession && !pageStore.isPublicView"
          text="Stop agent"
          :placement="Placement.TOP"
          :distance="10"
          class="ml-1">
          <Button
            :btn-style="ButtonStyle.SECONDARY"
            :size="ButtonSize.SMALL"
            is-contrast
            :icon="StopIcon"
            :icon-size="IconSize.S"
            :working="stoppingAgentSessionDuid === activeAgentSession.duid"
            a11y-label="Stop agent"
            class="!p-0.5 print:hidden"
            @click.stop="stopAgentSession" />
        </Tooltip>
        <Tooltip
          v-if="isTaskDetailMode && singleAgentAssignee && !pageStore.isPublicView"
          text="Open agent"
          :placement="Placement.TOP"
          :distance="10"
          class="ml-1">
          <Button
            :btn-style="ButtonStyle.SECONDARY"
            :size="ButtonSize.SMALL"
            is-contrast
            :icon="OpenInternalLinkIcon"
            :icon-size="IconSize.S"
            a11y-label="Open agent"
            class="!p-0.5 print:hidden"
            @click.stop="navigateToAgent" />
        </Tooltip>
        <Tooltip v-if="isRecommendationMode" text="Clear AI recommendation" class="ml-1">
          <span
            class="rounded-sm hover:bg-recommendation-base/30 dark:hover:bg-recommendation-base/40"
            data-global-popper-ignore
            @click.stop="rejectRecommendation"
            @keydown.enter.stop="rejectRecommendation">
            <XIcon class="icon-xs" />
          </span>
        </Tooltip>
      </div>
    </Tooltip>
  </MultiselectDropdownMenu>
</template>
